/**
 * src/api/mutationExecutors.ts — Wire each known mutation type to the
 * concrete API call. Imported once at app boot (in App.tsx); the queue
 * itself stays decoupled from the API surface.
 *
 * Adding a new offline-tolerant mutation = adding a key here + calling
 * enqueueMutation({ type: 'foo', payload }) from the relevant hook.
 */

import { createOrder, orderNewCrates, updateMyProfile, updateOrder } from './atticApi';
import { registerMutationExecutor, PermanentMutationError } from './mutationQueue';
import type { ScheduleOrderPayload } from '../types/order';
import type { PickupMethod } from './atticApi';

// Payload contracts for each registered mutation type. Keep the shapes
// minimal — anything the executor needs to call the API.
export interface UpdateOrderPayload {
  id: string;
  fields: Record<string, unknown>;
}

export interface UpdateProfilePayload {
  fields: Record<string, unknown>;
}

export interface OrderNewCratesPayload {
  requested_crate_count: number;
  scheduled_date: string;
  time_window: string;
  pickup_address?: string | undefined;
  delivery_instructions?: string | undefined;
  pickup_method?: PickupMethod | undefined;
}

/**
 * Decide whether a thrown error is a "user mistake" (4xx → permanent) or
 * a "server / network blip" (transient → retry). Anything that looks like
 * a 4xx in the message is treated as permanent so the queue doesn't grind
 * forever on a request the server will never accept.
 */
function classifyError(err: unknown): Error {
  const e = err instanceof Error ? err : new Error(String(err));
  // atticApi normalizes Harper errors; messages typically include the status.
  if (/\b4\d\d\b/.test(e.message)) return new PermanentMutationError(e.message);
  return e;
}

let installed = false;

export function installMutationExecutors(): void {
  if (installed) return;
  installed = true;

  registerMutationExecutor<ScheduleOrderPayload>('schedule_order', async (payload) => {
    try {
      return await createOrder({
        type: payload.type,
        crate_ids: payload.crate_ids,
        scheduled_date: payload.scheduled_date,
        window_id: payload.window_id,
        displayed_fee_cents: payload.displayed_fee_cents,
        time_window: payload.time_window,
        pickup_address: payload.pickup_address,
        pickup_method: payload.pickup_method,
      });
    } catch (err) {
      throw classifyError(err);
    }
  });

  registerMutationExecutor<UpdateOrderPayload>('update_order', async (payload) => {
    try {
      return await updateOrder(payload.id, payload.fields);
    } catch (err) {
      throw classifyError(err);
    }
  });

  registerMutationExecutor<UpdateProfilePayload>('update_profile', async (payload) => {
    try {
      return await updateMyProfile(payload.fields);
    } catch (err) {
      throw classifyError(err);
    }
  });

  registerMutationExecutor<OrderNewCratesPayload>('order_new_crates', async (payload) => {
    try {
      return await orderNewCrates(payload);
    } catch (err) {
      throw classifyError(err);
    }
  });
}
